import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, serverTimestamp } from 'firebase/firestore';
import { creerNotification } from './Notifications';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';
import FormulaireVisite from './FormulaireVisite';

const JOURS = ['Samedi', 'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi'];
const COULEUR = '#C0392B';
const VERT = '#3B6D11';

function getLundiSemaine(offset = 0) {
  const d = new Date();
  const jour = d.getDay();
  const diff = d.getDate() - ((jour + 1) % 7) + offset * 7;
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date) {
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function getDateJour(lundi, jourIndex) {
  const d = new Date(lundi);
  d.setDate(d.getDate() + jourIndex);
  return d;
}

function getJourIndex() {
  const jour = new Date().getDay();
  // 6=sam=0, 0=dim=1, 1=lun=2, 2=mar=3, 3=mer=4, 4=jeu=5
  const map = { 6: 0, 0: 1, 1: 2, 2: 3, 3: 4, 4: 5 };
  return map[jour] ?? 0;
}

export default function PlanningSemaine({ user, isManager, onClose }) {
  const [semaine, setSemaine] = useState(0);
  const [planning, setPlanning] = useState({});
  const [clients, setClients] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [plannings, setPlannings] = useState([]);
  const [jourSelectionne, setJourSelectionne] = useState(getJourIndex());
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const { lang, isRTL } = useLang();
  const [saving, setSaving] = useState(false);
  const [commFiltre, setCommFiltre] = useState(user.uid);
  const [statut, setStatut] = useState('brouillon');

  // Actions du jour
  const [actionsJour, setActionsJour] = useState([]);
  const [actionSelectionnee, setActionSelectionnee] = useState(null);
  const [showFormVisite, setShowFormVisite] = useState(false);
  const [clientVisite, setClientVisite] = useState(null);
  const [showReporter, setShowReporter] = useState(false);
  const [nouvelleDate, setNouvelleDate] = useState('');
  const [commentaireAppel, setCommentaireAppel] = useState('');
  const [showCommentaire, setShowCommentaire] = useState(false);
  const [onglet, setOnglet] = useState('planning'); // 'planning' | 'actions'

  const lundi = getLundiSemaine(semaine);

  useEffect(() => { charger(); }, [semaine, commFiltre]);

  const charger = async () => {
    setLoading(true);
    try {
      let cSnap;
      if (isManager) {
        cSnap = await getDocs(collection(db, 'clients'));
        const uSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
        setCommerciaux(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else {
        cSnap = await getDocs(query(collection(db, 'clients'), where('commercialIds', 'array-contains', user.uid)));
      }
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const commId = isManager ? commFiltre : user.uid;
      const pSnap = await getDocs(query(
        collection(db, 'plannings'),
        where('commercialId', '==', commId),
        where('semaine', '==', lundi.toISOString())
      ));

      if (pSnap.docs.length > 0) {
        const p = pSnap.docs[0];
        setPlanning(p.data().jours || {});
        setStatut(p.data().statut || 'brouillon');
      } else {
        setPlanning({});
        setStatut('brouillon');
      }

      if (isManager) {
        const allSnap = await getDocs(query(
          collection(db, 'plannings'),
          where('semaine', '==', lundi.toISOString())
        ));
        setPlannings(allSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }

      // Charger actions du jour
      if (!isManager) {
        const today = new Date().toISOString().split('T')[0];
        const aSnap = await getDocs(query(
          collection(db, 'actions'),
          where('commercialId', '==', user.uid),
          where('statut', '==', 'A FAIRE')
        ));
        const actions = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Filtrer actions du jour et en retard
        const actionsFiltre = actions.filter(a => a.dateAction && a.dateAction <= today);
        setActionsJour(actionsFiltre);
      }
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const ajouterClient = (clientId) => {
    const jourKey = JOURS[jourSelectionne];
    setPlanning(p => ({ ...p, [jourKey]: [...(p[jourKey] || []), clientId] }));
    setShowPicker(false);
  };

  const retirerClient = (jourKey, clientId) => {
    setPlanning(p => ({ ...p, [jourKey]: (p[jourKey] || []).filter(id => id !== clientId) }));
  };

  const getNomClient = (id) => clients.find(c => c.id === id)?.nom || id;
  const getTelClient = (id) => clients.find(c => c.id === id)?.telephone || '';

  const totalVisitesPlanifiees = Object.values(planning).reduce((s, arr) => s + arr.length, 0);

  const sauvegarder = async (nouveauStatut) => {
    setSaving(true);
    try {
      const commId = isManager ? commFiltre : user.uid;
      const pSnap = await getDocs(query(
        collection(db, 'plannings'),
        where('commercialId', '==', commId),
        where('semaine', '==', lundi.toISOString())
      ));

      const data = {
        commercialId: commId,
        semaine: lundi.toISOString(),
        jours: planning,
        statut: nouveauStatut || statut,
        updatedAt: serverTimestamp(),
      };

      if (pSnap.docs.length > 0) {
        await updateDoc(doc(db, 'plannings', pSnap.docs[0].id), data);
      } else {
        await addDoc(collection(db, 'plannings'), { ...data, createdAt: serverTimestamp() });
      }

      if (nouveauStatut === 'soumis') {
        try {
          const { getDocs: gd, query: q, collection: col, where: wh } = await import('firebase/firestore');
          const managers = await gd(q(col(db, 'users'), wh('role', '==', 'manager')));
          for (const m of managers.docs) {
            await creerNotification(m.id, 'planning_soumis', 'Nouveau planning soumis', `${user.prenom} ${user.nom} a soumis son planning pour validation.`);
          }
        } catch(e) {}
      }
      setStatut(nouveauStatut || statut);
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const validerPlanning = async (planningId, decision) => {
    try {
      const p = plannings.find(x => x.id === planningId);
      await updateDoc(doc(db, 'plannings', planningId), {
        statut: decision,
        validePar: user.uid,
        valideAt: serverTimestamp(),
      });
      if (p) {
        if (decision === 'valide') {
          await creerNotification(p.commercialId, 'planning_valide', 'Planning valide !', 'Votre planning de la semaine a ete valide par le manager.');
        } else {
          await creerNotification(p.commercialId, 'planning_refuse', 'Planning refuse', 'Votre planning de la semaine a ete refuse. Veuillez le modifier et le resoumettre.');
        }
      }
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  // Actions
  const activerVisite = (action) => {
    const client = clients.find(c => c.id === action.clientId) || { id: action.clientId, nom: action.clientNom };
    setClientVisite(client);
    setActionSelectionnee(action);
    setShowFormVisite(true);
  };

  const marquerFaite = async (action, commentaire = '') => {
    try {
      await updateDoc(doc(db, 'actions', action.id), {
        statut: 'FAIT',
        commentaireCloture: commentaire,
        dateCloture: new Date().toISOString(),
      });
      setActionSelectionnee(null);
      setShowCommentaire(false);
      setCommentaireAppel('');
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const reporterAction = async (action) => {
    if (!nouvelleDate) return;
    try {
      // Marquer ancienne action comme reportée
      await updateDoc(doc(db, 'actions', action.id), {
        statut: 'ANNULE',
        commentaireCloture: 'Reportee au ' + nouvelleDate,
      });
      // Créer nouvelle action
      await addDoc(collection(db, 'actions'), {
        commercialId: action.commercialId,
        clientId: action.clientId,
        clientNom: action.clientNom,
        action: action.action,
        dateAction: nouvelleDate,
        responsable: action.responsable || 'Commercial',
        rappels: action.rappels || [],
        statut: 'A FAIRE',
        createdAt: serverTimestamp(),
        reporteeDepuis: action.id,
      });
      setActionSelectionnee(null);
      setShowReporter(false);
      setNouvelleDate('');
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const appelClient = (tel) => {
    if (tel) window.location.href = `tel:${tel}`;
  };

  const statutColor = {
    brouillon: { bg: '#f5f5f5', color: '#888', label: 'Brouillon' },
    soumis: { bg: '#FAEEDA', color: '#854F0B', label: 'En attente de validation' },
    valide: { bg: '#EAF3DE', color: '#3B6D11', label: 'Valide par le manager' },
    refuse: { bg: '#FCEBEB', color: '#A32D2D', label: 'Refuse - a modifier' },
  };

  const clientsDisponibles = clients.filter(c => {
    const jourKey = JOURS[jourSelectionne];
    return !(planning[jourKey] || []).includes(c.id);
  });

  const actionsRetard = actionsJour.filter(a => a.dateAction < new Date().toISOString().split('T')[0]);
  const actionsAujourdhui = actionsJour.filter(a => a.dateAction === new Date().toISOString().split('T')[0]);

  if (showFormVisite && clientVisite) {
    return (
      <FormulaireVisite
        user={user}
        client={clientVisite}
        onClose={() => { setShowFormVisite(false); setClientVisite(null); }}
        onSave={async () => {
          if (actionSelectionnee) await marquerFaite(actionSelectionnee, 'Visite effectuee');
          await charger();
        }}
      />
    );
  }

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>Planning semaine</div>
          <div style={styles.topbarSub}>
            {formatDate(lundi)} — {formatDate(getDateJour(lundi, 5))}
          </div>
        </div>
        <div style={styles.semaineNav}>
          <button style={styles.navBtn} onClick={() => setSemaine(s => s - 1)}>‹</button>
          <button style={styles.navBtn} onClick={() => setSemaine(0)}>Auj.</button>
          <button style={styles.navBtn} onClick={() => setSemaine(s => s + 1)}>›</button>
        </div>
      </div>

      {/* ONGLETS Planning / Actions */}
      {!isManager && (
        <div style={styles.ongletBar}>
          <button style={{ ...styles.ongletBtn, ...(onglet === 'planning' ? styles.ongletBtnActive : {}) }}
            onClick={() => setOnglet('planning')}>
            📅 Planning
          </button>
          <button style={{ ...styles.ongletBtn, ...(onglet === 'actions' ? styles.ongletBtnActive : {}), position: 'relative' }}
            onClick={() => setOnglet('actions')}>
            ✅ Actions
            {actionsJour.length > 0 && (
              <span style={styles.badge}>{actionsJour.length}</span>
            )}
          </button>
        </div>
      )}

      {/* FILTRE COMMERCIAL (manager) */}
      {isManager && commerciaux.length > 0 && (
        <div style={styles.filtreBar}>
          <select style={styles.filtreSelect} value={commFiltre} onChange={e => setCommFiltre(e.target.value)}>
            {commerciaux.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
          </select>
        </div>
      )}

      {/* ===== ONGLET PLANNING ===== */}
      {(onglet === 'planning' || isManager) && (
        <>
          <div style={{ ...styles.statutBar, background: statutColor[statut]?.bg }}>
            <span style={{ fontSize: 13, color: statutColor[statut]?.color, fontWeight: 500 }}>
              {statutColor[statut]?.label}
            </span>
            <span style={{ fontSize: 12, color: '#888' }}>{totalVisitesPlanifiees} visite(s) planifiee(s)</span>
          </div>

          <div style={styles.joursBar}>
            {JOURS.map((j, i) => {
              const date = getDateJour(lundi, i);
              const count = (planning[j] || []).length;
              const estAujourdhui = date.toDateString() === new Date().toDateString();
              return (
                <div key={j} style={{ ...styles.jourBtn, ...(jourSelectionne === i ? styles.jourBtnActive : {}), ...(estAujourdhui ? styles.jourBtnToday : {}) }}
                  onClick={() => setJourSelectionne(i)}>
                  <div style={styles.jourNom}>{j.slice(0, 3)}</div>
                  <div style={styles.jourDate}>{formatDate(date)}</div>
                  {count > 0 && <div style={styles.jourBadge}>{count}</div>}
                </div>
              );
            })}
          </div>

          <div style={styles.content}>
            <div style={styles.jourTitre}>{JOURS[jourSelectionne]} — {formatDate(getDateJour(lundi, jourSelectionne))}</div>

            {(planning[JOURS[jourSelectionne]] || []).length === 0 && (
              <div style={styles.empty}>
                <div style={{ fontSize: 28 }}>📅</div>
                <div style={{ color: '#888', marginTop: 6 }}>Aucune visite planifiee</div>
              </div>
            )}

            {(planning[JOURS[jourSelectionne]] || []).map((clientId, i) => {
              const client = clients.find(c => c.id === clientId);
              return (
                <div key={i} style={styles.clientCard}>
                  <div style={styles.clientAv}>{client?.nom?.[0]?.toUpperCase() || '?'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.clientNom}>{client?.nom || clientId}</div>
                    <div style={styles.clientSub}>{client?.type || client?.statut} · {client?.commune}</div>
                  </div>
                  {statut !== 'valide' && (
                    <button style={styles.retirerBtn} onClick={() => retirerClient(JOURS[jourSelectionne], clientId)}>✕</button>
                  )}
                </div>
              );
            })}

            {statut !== 'valide' && (
              <button style={styles.addBtn} onClick={() => setShowPicker(true)}>
                + Ajouter un client a cette journee
              </button>
            )}
          </div>

          <div style={styles.actionsBar}>
            {!isManager && (
              <>
                <button style={styles.btnSave} onClick={() => sauvegarder('brouillon')} disabled={saving}>
                  {saving ? '...' : 'Sauvegarder'}
                </button>
                {statut !== 'valide' && (
                  <button style={styles.btnSubmit} onClick={() => sauvegarder('soumis')} disabled={saving}>
                    {saving ? '...' : 'Soumettre au manager'}
                  </button>
                )}
              </>
            )}
            {isManager && (
              <>
                {plannings.filter(p => p.statut === 'soumis').map(p => (
                  <div key={p.id} style={styles.validRow}>
                    <span style={{ fontSize: 12, color: '#854F0B', flex: 1 }}>Planning en attente</span>
                    <button style={styles.btnValider} onClick={() => validerPlanning(p.id, 'valide')}>Valider</button>
                    <button style={styles.btnRefuser} onClick={() => validerPlanning(p.id, 'refuse')}>Refuser</button>
                  </div>
                ))}
                <button style={styles.btnSave} onClick={() => sauvegarder()} disabled={saving}>
                  {saving ? '...' : 'Sauvegarder modifications'}
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* ===== ONGLET ACTIONS ===== */}
      {onglet === 'actions' && !isManager && (
        <div style={styles.content}>

          {actionsJour.length === 0 && (
            <div style={styles.empty}>
              <div style={{ fontSize: 32 }}>✅</div>
              <div style={{ color: '#888', marginTop: 8 }}>Aucune action prevue aujourd'hui</div>
            </div>
          )}

          {/* Actions en retard */}
          {actionsRetard.length > 0 && (
            <>
              <div style={styles.sectionLabel}>⚠️ En retard ({actionsRetard.length})</div>
              {actionsRetard.map(a => (
                <ActionCard key={a.id} action={a} clients={clients}
                  onActiver={() => activerVisite(a)}
                  onAppeler={() => { setActionSelectionnee(a); setShowCommentaire(true); appelClient(getTelClient(a.clientId)); }}
                  onReporter={() => { setActionSelectionnee(a); setShowReporter(true); }}
                  getTelClient={getTelClient}
                  estRetard={true}
                />
              ))}
            </>
          )}

          {/* Actions aujourd'hui */}
          {actionsAujourdhui.length > 0 && (
            <>
              <div style={styles.sectionLabel}>📅 Aujourd'hui ({actionsAujourdhui.length})</div>
              {actionsAujourdhui.map(a => (
                <ActionCard key={a.id} action={a} clients={clients}
                  onActiver={() => activerVisite(a)}
                  onAppeler={() => { setActionSelectionnee(a); setShowCommentaire(true); appelClient(getTelClient(a.clientId)); }}
                  onReporter={() => { setActionSelectionnee(a); setShowReporter(true); }}
                  getTelClient={getTelClient}
                  estRetard={false}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* MODAL REPORTER */}
      {showReporter && actionSelectionnee && (
        <div style={styles.pickerOverlay}>
          <div style={styles.pickerModal}>
            <div style={styles.pickerHeader}>
              <div style={styles.pickerTitre}>Reporter l'action</div>
              <button style={styles.pickerClose} onClick={() => { setShowReporter(false); setNouvelleDate(''); }}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Action : <strong>{actionSelectionnee.action}</strong><br />
              Client : <strong>{actionSelectionnee.clientNom}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Nouvelle date :</div>
            <input type="date" style={styles.dateInput}
              value={nouvelleDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => setNouvelleDate(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button style={styles.btnSave} onClick={() => { setShowReporter(false); setNouvelleDate(''); }}>Annuler</button>
              <button style={{ ...styles.btnSubmit, opacity: nouvelleDate ? 1 : 0.4 }}
                onClick={() => reporterAction(actionSelectionnee)} disabled={!nouvelleDate}>
                Reporter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COMMENTAIRE APRES APPEL */}
      {showCommentaire && actionSelectionnee && (
        <div style={styles.pickerOverlay}>
          <div style={styles.pickerModal}>
            <div style={styles.pickerHeader}>
              <div style={styles.pickerTitre}>Resultat de l'appel</div>
              <button style={styles.pickerClose} onClick={() => { setShowCommentaire(false); setCommentaireAppel(''); }}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Client : <strong>{actionSelectionnee.clientNom}</strong>
            </div>
            <textarea style={{ ...styles.dateInput, height: 80, resize: 'none', fontFamily: 'system-ui' }}
              placeholder="Commentaire sur l'appel..."
              value={commentaireAppel}
              onChange={e => setCommentaireAppel(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button style={styles.btnSave} onClick={() => { setShowCommentaire(false); setCommentaireAppel(''); }}>Annuler</button>
              <button style={styles.btnSubmit} onClick={() => marquerFaite(actionSelectionnee, commentaireAppel)}>
                Marquer comme faite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PICKER CLIENT */}
      {showPicker && (
        <div style={styles.pickerOverlay}>
          <div style={styles.pickerModal}>
            <div style={styles.pickerHeader}>
              <div style={styles.pickerTitre}>Ajouter un client — {JOURS[jourSelectionne]}</div>
              <button style={styles.pickerClose} onClick={() => setShowPicker(false)}>✕</button>
            </div>
            {clientsDisponibles.length === 0 && (
              <div style={styles.empty}>Tous les clients sont deja ajoutes ce jour</div>
            )}
            {clientsDisponibles.map(c => (
              <div key={c.id} style={styles.pickerItem} onClick={() => ajouterClient(c.id)}>
                <div style={styles.pickerAv}>{c.nom?.[0]?.toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={styles.clientNom}>{c.nom}</div>
                  <div style={styles.clientSub}>{c.type || c.statut} · {c.commune}</div>
                </div>
                <span style={{ color: COULEUR, fontSize: 20 }}>+</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Composant ActionCard
function ActionCard({ action, clients, onActiver, onAppeler, onReporter, getTelClient, estRetard }) {
  const client = clients.find(c => c.id === action.clientId);
  const tel = getTelClient(action.clientId);
  const isAppel = action.action?.toLowerCase().includes('appel') || action.action?.toLowerCase().includes('rappel');

  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: estRetard ? '1px solid #F5C6C6' : '1px solid #eee' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: estRetard ? '#FCEBEB' : '#EAF3DE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
          {isAppel ? '📞' : '📍'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2833' }}>{action.action}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
            {action.clientNom} · {estRetard ? 'En retard' : action.dateAction}
          </div>
          {tel && <div style={{ fontSize: 11, color: '#185FA5', marginTop: 2 }}>📞 {tel}</div>}
        </div>
      </div>

      {isAppel ? (
        // Action de type appel
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ flex: 1, padding: '9px', background: '#185FA5', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            onClick={onAppeler}>
            📞 Appeler {tel ? tel : ''}
          </button>
          <button style={{ padding: '9px 12px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 8, color: '#666', fontSize: 12, cursor: 'pointer' }}
            onClick={onReporter}>
            📅 Reporter
          </button>
        </div>
      ) : (
        // Action de type visite
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ flex: 1, padding: '9px 6px', background: VERT, border: 'none', borderRadius: 8, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
            onClick={onActiver}>
            📍 Visiter
          </button>
          <button style={{ flex: 1, padding: '9px 6px', background: '#185FA5', border: 'none', borderRadius: 8, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
            onClick={onAppeler}>
            📞 Appeler
          </button>
          <button style={{ flex: 1, padding: '9px 6px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 8, color: '#666', fontSize: 11, cursor: 'pointer' }}
            onClick={onReporter}>
            📅 Reporter
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: COULEUR, padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.8, marginTop: 2 },
  semaineNav: { display: 'flex', gap: 4, alignItems: 'center' },
  navBtn: { background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', fontSize: 14, width: 28, height: 28, cursor: 'pointer' },
  ongletBar: { display: 'flex', background: '#fff', borderBottom: '1px solid #eee' },
  ongletBtn: { flex: 1, padding: '12px', border: 'none', background: 'none', fontSize: 13, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent', position: 'relative' },
  ongletBtnActive: { color: COULEUR, borderBottomColor: COULEUR, fontWeight: 600 },
  badge: { position: 'absolute', top: 6, right: 20, background: COULEUR, color: '#fff', fontSize: 9, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  filtreBar: { background: '#fff', padding: '8px 12px', borderBottom: '1px solid #eee' },
  filtreSelect: { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 },
  statutBar: { padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  joursBar: { display: 'flex', background: '#fff', borderBottom: '1px solid #eee', overflowX: 'auto' },
  jourBtn: { flex: 1, padding: '8px 4px', textAlign: 'center', cursor: 'pointer', borderBottom: '2px solid transparent', minWidth: 56, position: 'relative' },
  jourBtnActive: { borderBottomColor: COULEUR },
  jourBtnToday: { background: '#fff8f8' },
  jourNom: { fontSize: 11, fontWeight: 600, color: '#666' },
  jourDate: { fontSize: 10, color: '#aaa', marginTop: 2 },
  jourBadge: { position: 'absolute', top: 4, right: 4, background: COULEUR, color: '#fff', fontSize: 9, width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 8 },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 },
  jourTitre: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  empty: { textAlign: 'center', padding: '2rem', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  clientCard: { background: '#fff', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  clientAv: { width: 34, height: 34, borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: COULEUR, flexShrink: 0 },
  clientNom: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  clientSub: { fontSize: 11, color: '#888', marginTop: 2 },
  retirerBtn: { background: '#FCEBEB', border: 'none', borderRadius: 6, color: '#A32D2D', fontSize: 12, cursor: 'pointer', padding: '4px 8px' },
  addBtn: { width: '100%', padding: 12, border: '1px dashed #ccc', borderRadius: 10, background: 'none', color: '#666', fontSize: 13, cursor: 'pointer' },
  actionsBar: { padding: '10px 12px', background: '#fff', borderTop: '1px solid #eee', display: 'flex', gap: 8, flexDirection: 'column' },
  btnSave: { padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' },
  btnSubmit: { padding: 11, background: COULEUR, border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  validRow: { display: 'flex', alignItems: 'center', gap: 8, background: '#FAEEDA', borderRadius: 10, padding: '10px 12px' },
  btnValider: { padding: '7px 12px', background: '#EAF3DE', border: 'none', borderRadius: 8, color: '#3B6D11', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnRefuser: { padding: '7px 12px', background: '#FCEBEB', border: 'none', borderRadius: 8, color: '#A32D2D', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  pickerOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end' },
  pickerModal: { background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '16px', maxHeight: '70vh', overflowY: 'auto' },
  pickerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  pickerTitre: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  pickerClose: { background: '#f5f5f5', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer' },
  pickerItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  pickerAv: { width: 34, height: 34, borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: COULEUR, flexShrink: 0 },
  dateInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', boxSizing: 'border-box' },
};
